import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerSummaryApi } from "../../../../../../../../../../services/Api/Partner/partnerTypes";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import { downloadFile } from "../../../../../../../../../../utils";
import PayoffInfo from "../../../../../../../../components/Admin/Payoff/PayoffInfo";
import SendViaEmail from "../../../../../../../Admin/pages/Payoffs/pages/components/SendViaEmail";

interface MatchParams {
	payoffId: string;
}

const PartnerPreviewPayoffDetailsPage = () => {
	const { payoffId } = useParams<MatchParams>();
	const { t } = useTranslation("admin");
	const { addFlash } = useFlash();
	const partner = useSelector(selectPartner);
	const [payoff, setPayoff] = useState<PartnerSummaryApi | undefined>(undefined);
	const [showModal, setShowModal] = useState<undefined | PartnerSummaryApi>(undefined);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchPayoff();
	}, []);

	const fetchPayoff = async () => {
		try {
			const res = await partnerApi.getPayoff(Number(partner?.id), payoffId);
			setPayoff(res);
			handleChangeTabTitle(
				`${res.number} | ${t("modules.payoff.header.title")} | ${t(
					"modules.partner.field.partner_panel.title"
				)}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!payoff) return <LoadingContainer />;

	const sendEmail = () => {
		setShowModal(payoff);
	};

	const downloadPdf = async () => {
		await downloadFile(
			() => partnerApi.getSummaryPdf(payoff?.id),
			`${t("common.word.payoff")}_${payoff.number}.pdf`,
			addFlash
		);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.send_payoff_via_email"),
			action: sendEmail,
			dropdown: true,
		},
		{
			title: t("common.action.download_pdf"),
			action: downloadPdf,
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.send_payoff_via_email"),
			action: sendEmail,
		},
		{
			title: t("common.action.download_pdf"),
			action: downloadPdf,
		},
	];

	const renderTitle = () => (
		<>
			{t("modules.payoff.header.title")} {payoff.number} <FormatResourceStatus status={payoff.status} />
		</>
	);

	return (
		<div className={"content-preview"}>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			<PayoffInfo payoff={payoff} fetchPayoff={fetchPayoff} isAdminMode={false} />
			{showModal && <SendViaEmail onHide={() => setShowModal(undefined)} summary={showModal} />}
		</div>
	);
};

export default PartnerPreviewPayoffDetailsPage;
